import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authApi, errMsg } from "@/lib/api";
import { homeFor, useAuth } from "@/context/AuthContext";
import { InstallAppButton } from "@/components/InstallApp";

const Shell = ({ title, sub, children }) => (
  <div className="auth-bg">
    <div className="auth-card" data-testid="auth-card">
      <img src="/experior-logo.svg" alt="Experior Financial Group" className="h-12 mb-3" />
      <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Financial Planning Portal</p>
      <h1 className="font-heading text-2xl font-extrabold text-[#0b2545] mt-1">{title}</h1>
      {sub && <p className="text-sm text-slate-500 mt-1">{sub}</p>}
      <div className="mt-5 space-y-3">{children}</div>
      <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between gap-2">
        <span className="text-[11px] text-slate-500">Use it like an app on your phone</span>
        <InstallAppButton label="Install app" />
      </div>
      <p className="text-[11px] text-slate-400 mt-4">Operated by Shalindra K C, ExpInsured Finance · Experior Financial Group</p>
    </div>
  </div>
);

const Field = ({ id, label, ...p }) => (
  <div className="space-y-1"><Label htmlFor={id} className="field-label">{label}</Label><Input id={id} data-testid={id} className="h-10 bg-white" {...p} /></div>
);

const Submit = ({ busy, children, testId }) => (
  <Button type="submit" disabled={busy} className="w-full h-10" data-testid={testId}>{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}{children}</Button>
);

const Err = ({ msg }) => (msg ? <p className="text-sm text-rose-600" data-testid="auth-error">{msg}</p> : null);

export function LoginPage() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "", code: "" });
  const [mfa, setMfa] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { if (user) nav(homeFor(user), { replace: true }); }, [user, nav]);
  const run = async (e, fn) => { e.preventDefault(); setBusy(true); setErr(""); try { await fn(); } catch (x) { setErr(errMsg(x)); } setBusy(false); };
  const login = (e) => run(e, async () => {
    const r = await authApi.login(f.email, f.password);
    if (r.mfa_required) { setMfa(r.mfa_token); toast.success("We emailed you a 6-digit sign-in code"); } else setUser(r.user);
  });
  const verify = (e) => run(e, async () => setUser((await authApi.mfa(mfa, f.code)).user));
  if (mfa) return (
    <Shell title="Enter your sign-in code" sub={`We sent a 6-digit code to ${f.email}. It expires in 10 minutes.`}>
      <form onSubmit={verify} className="space-y-3">
        <Field id="mfa-code" label="6-digit code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.replace(/\D/g, "") })} autoFocus />
        <Err msg={err} /><Submit busy={busy} testId="mfa-submit-button">Verify and sign in</Submit>
        <button type="button" className="text-xs text-[#0072C6] underline" onClick={() => { setMfa(null); setF({ ...f, code: "" }); }} data-testid="mfa-back-button">Start over</button>
      </form>
    </Shell>
  );
  return (
    <Shell title="Sign in" sub="Access is by invitation only.">
      <form onSubmit={login} className="space-y-3">
        <Field id="login-email" label="Email" type="email" autoComplete="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required />
        <Field id="login-password" label="Password" type="password" autoComplete="current-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required />
        <Err msg={err} /><Submit busy={busy} testId="login-submit-button">Sign in</Submit>
      </form>
      <Link to="/forgot-password" className="text-xs text-[#0072C6] underline" data-testid="forgot-password-link">Forgot your password?</Link>
    </Shell>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => { e.preventDefault(); setBusy(true); await authApi.forgot(email).catch(() => {}); setSent(true); setBusy(false); };
  return (
    <Shell title="Reset your password" sub="Enter your email and we'll send a reset link if an account exists.">
      {sent ? <p className="text-sm text-emerald-700" data-testid="forgot-sent">If an account exists for {email}, a reset link is on its way. It expires in 1 hour.</p> : (
        <form onSubmit={submit} className="space-y-3">
          <Field id="forgot-email" label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          <Submit busy={busy} testId="forgot-submit-button">Send reset link</Submit>
        </form>
      )}
      <Link to="/login" className="text-xs text-[#0072C6] underline" data-testid="back-to-login-link">Back to sign in</Link>
    </Shell>
  );
}

const PasswordForm = ({ onSubmit, cta, testId }) => {
  const [p, setP] = useState({ a: "", b: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (p.a.length < 10) return setErr("Use at least 10 characters.");
    if (p.a !== p.b) return setErr("Passwords don't match.");
    setBusy(true);
    try { await onSubmit(p.a); } catch (x) { setErr(errMsg(x)); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="space-y-3">
      <Field id="new-password" label="New password (10+ characters)" type="password" autoComplete="new-password" value={p.a} onChange={(e) => setP({ ...p, a: e.target.value })} required />
      <Field id="confirm-password" label="Confirm password" type="password" autoComplete="new-password" value={p.b} onChange={(e) => setP({ ...p, b: e.target.value })} required />
      <Err msg={err} /><Submit busy={busy} testId={testId}>{cta}</Submit>
    </form>
  );
};

export function ResetPasswordPage() {
  const { token } = useParams();
  const nav = useNavigate();
  return (
    <Shell title="Choose a new password">
      <PasswordForm cta="Save new password" testId="reset-submit-button" onSubmit={async (pw) => { await authApi.reset(token, pw); toast.success("Password updated. Please sign in."); nav("/login"); }} />
    </Shell>
  );
}

export function AcceptInvitePage() {
  const { token } = useParams();
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { authApi.invite(token).then(setInv).catch((x) => setErr(errMsg(x))); }, [token]);
  if (err) return <Shell title="Invitation unavailable"><p className="text-sm text-rose-600" data-testid="invite-error">{err}</p><Link to="/login" className="text-xs text-[#0072C6] underline">Go to sign in</Link></Shell>;
  if (!inv) return <Shell title="Checking your invitation…" />;
  return (
    <Shell title={`Welcome, ${inv.name}`} sub={`Create a password for ${inv.email} to start your confidential financial assessment.`}>
      <PasswordForm cta="Create my account" testId="accept-invite-button" onSubmit={async (pw) => { const r = await authApi.accept(token, pw); setUser(r.user); nav("/portal"); }} />
    </Shell>
  );
}
